import { Box, ButtonBase, Divider, Stack, Typography } from "@mui/material";
import type { JSX } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { BackButton } from "@/components/BackButton/BackButton";

interface ValuationAnalysisStickyHeaderProps {
  topOffset?: number | string;
}

export const ValuationAnalysisStickyHeader = ({ topOffset = 0 }: ValuationAnalysisStickyHeaderProps): JSX.Element => {
  const { t: tPropertyDetails } = useTranslation("PropertyDetailsPanel");
  const { t: tPricePositioning } = useTranslation("PricePositioningPanel");
  const { t: tDemandScore } = useTranslation("DemandScorePanel");
  const { t: tIndicativeMarketValuation } = useTranslation("IndicativeMarketValuationPanel");
  const { t: tReportGeneration } = useTranslation("ValuationReportGenerationPanel");
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const { valuationId } = useParams<{ valuationId: string }>();
  const sections = useMemo(
    () => [
      { id: "valuation-analysis-property-details", label: tPropertyDetails("overview") },
      { id: "valuation-analysis-market-analysis", label: tPricePositioning("title") },
      { id: "valuation-analysis-demand-score", label: tDemandScore("title") },
      { id: "valuation-analysis-indicative-market-valuation", label: tIndicativeMarketValuation("title") },
      {
        id: "valuation-analysis-report-configuration",
        label: tReportGeneration("reportPanel.configuration.mainLabel"),
      },
    ],
    [tDemandScore, tPricePositioning, tPropertyDetails, tReportGeneration, tIndicativeMarketValuation],
  );
  const [activeSectionId, setActiveSectionId] = useState<string>(sections[0].id);

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: topOffset,
        zIndex: 10,
        px: "30px",
        backgroundColor: "background.paper",
        boxShadow: "0px 4px 2px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ minWidth: 0, overflowX: "auto" }}>
        <BackButton
          label={tCommon("buttons.back")}
          onClick={() => {
            if (valuationId) {
              navigate(`/valuations/${valuationId}`);
              return;
            }
            navigate(-1);
          }}
          data-cy="valuation-analysis-back-button"
        />
        <Stack direction="row" alignItems="center" spacing={"20px"} sx={{ minWidth: 0 }}>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              alignSelf: "center",
              borderRightWidth: "1px",
              borderColor: (theme) => theme.semanticColors.border.action,
              height: "42px",
            }}
          />
          {sections.map((section) => (
            <ButtonBase
              key={section.id}
              onClick={() => {
                setActiveSectionId(section.id);
                document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              sx={(theme) => ({
                flexShrink: 0,
                minWidth: "150px",
                px: "20px",
                py: "15px",
                display: "flex",
                justifyContent: "center",
                borderBottom:
                  activeSectionId === section.id
                    ? `2px solid ${theme.semanticColors.border.action}`
                    : "2px solid transparent",
                color: theme.semanticColors.interactive.primary,
              })}
            >
              <Typography
                sx={(theme) => ({
                  fontFamily: theme.typography.fontFamily,
                  fontSize: "15px",
                  lineHeight: "24px",
                  fontWeight: 600,
                  color: theme.semanticColors.interactive.primary,
                  whiteSpace: "nowrap",
                })}
              >
                {section.label}
              </Typography>
            </ButtonBase>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};
